import { useState, FormEvent, useEffect } from 'react'
import { campaignApi } from '../api'
import type { ApiUsageEstimate } from '../types'
import { useNavigate, useParams } from 'react-router-dom'

export function CampaignForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const [name, setName] = useState('')
  const [targetSubreddit, setTargetSubreddit] = useState('')
  const [monitoredSubreddits, setMonitoredSubreddits] = useState('')
  const [keywords, setKeywords] = useState('')
  const [writingStyle, setWritingStyle] = useState('')
  const [exampleComments, setExampleComments] = useState('')
  const [minDelay, setMinDelay] = useState('120')
  const [maxDelay, setMaxDelay] = useState('600')
  const [autoApprove, setAutoApprove] = useState(false)
  const [maxPostsPerHour, setMaxPostsPerHour] = useState('10')
  const [minUpvotes, setMinUpvotes] = useState('')
  const [estimate, setEstimate] = useState<ApiUsageEstimate | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isEdit && id) {
      loadCampaign(id)
    }
  }, [id, isEdit])

  const loadCampaign = async (campaignId: string) => {
    try {
      const data = await campaignApi.list()
      const campaign = data.campaigns.find((c) => c.id === campaignId)
      if (campaign) {
        setName(campaign.name)
        setTargetSubreddit(campaign.targetSubreddit)
        setMonitoredSubreddits(campaign.monitoredSubreddits.join(', '))
        setKeywords(campaign.keywords.join(', '))
        setWritingStyle(campaign.writingStyle)
        setExampleComments(campaign.exampleComments.join('\n\n'))
        setMinDelay(campaign.minDelay.toString())
        setMaxDelay(campaign.maxDelay.toString())
        setAutoApprove(campaign.autoApprove)
        setMaxPostsPerHour(campaign.maxPostsPerHour.toString())
        setMinUpvotes(campaign.minUpvotes?.toString() || '')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load campaign')
    }
  }

  const handleEstimate = async () => {
    try {
      const config = {
        monitoredSubreddits: monitoredSubreddits.split(',').map((s) => s.trim()),
        maxPostsPerHour: parseInt(maxPostsPerHour),
      }
      const data = await campaignApi.estimateUsage(config)
      setEstimate(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to estimate usage')
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const campaignData = {
        name,
        targetSubreddit,
        monitoredSubreddits: monitoredSubreddits.split(',').map((s) => s.trim()),
        keywords: keywords.split(',').map((k) => k.trim()),
        writingStyle,
        exampleComments: exampleComments
          .split('\n\n')
          .map((c) => c.trim())
          .filter((c) => c),
        minDelay: parseInt(minDelay),
        maxDelay: parseInt(maxDelay),
        autoApprove,
        maxPostsPerHour: parseInt(maxPostsPerHour),
        minUpvotes: minUpvotes ? parseInt(minUpvotes) : null,
        active: false,
      }

      if (isEdit && id) {
        await campaignApi.update(id, campaignData)
      } else {
        await campaignApi.create(campaignData)
      }

      navigate('/campaigns')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save campaign')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <h2>{isEdit ? 'Edit Campaign' : 'Create Campaign'}</h2>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Campaign Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            style={{ width: '100%', padding: '8px', fontSize: '14px' }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Target Subreddit (to promote) *
          </label>
          <input
            type="text"
            value={targetSubreddit}
            onChange={(e) => setTargetSubreddit(e.target.value)}
            placeholder="mentalhealthlawyers"
            required
            style={{ width: '100%', padding: '8px', fontSize: '14px' }}
          />
          <small style={{ color: '#666' }}>The subreddit you want to drive traffic to</small>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Monitored Subreddits (comma-separated) *
          </label>
          <input
            type="text"
            value={monitoredSubreddits}
            onChange={(e) => setMonitoredSubreddits(e.target.value)}
            placeholder="legaladvice, law, lawyers"
            required
            style={{ width: '100%', padding: '8px', fontSize: '14px' }}
          />
          <small style={{ color: '#666' }}>Subreddits to watch for relevant posts</small>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Keywords (comma-separated) *
          </label>
          <input
            type="text"
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
            placeholder="mental health, anxiety, depression"
            required
            style={{ width: '100%', padding: '8px', fontSize: '14px' }}
          />
          <small style={{ color: '#666' }}>Only comment on posts containing these keywords</small>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Writing Style *
          </label>
          <textarea
            value={writingStyle}
            onChange={(e) => setWritingStyle(e.target.value)}
            placeholder="Casual, helpful, empathetic. Use simple language..."
            required
            rows={3}
            style={{ width: '100%', padding: '8px', fontSize: '14px' }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Example Comments (one per paragraph)
          </label>
          <textarea
            value={exampleComments}
            onChange={(e) => setExampleComments(e.target.value)}
            placeholder="Example comment 1...&#10;&#10;Example comment 2..."
            rows={5}
            style={{ width: '100%', padding: '8px', fontSize: '14px' }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Min Delay (seconds) *
            </label>
            <input
              type="number"
              value={minDelay}
              onChange={(e) => setMinDelay(e.target.value)}
              required
              min="0"
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Max Delay (seconds) *
            </label>
            <input
              type="number"
              value={maxDelay}
              onChange={(e) => setMaxDelay(e.target.value)}
              required
              min="0"
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Max Posts Per Hour *
          </label>
          <input
            type="number"
            value={maxPostsPerHour}
            onChange={(e) => setMaxPostsPerHour(e.target.value)}
            required
            min="1"
            style={{ width: '100%', padding: '8px', fontSize: '14px' }}
          />
          <button
            type="button"
            onClick={handleEstimate}
            style={{
              marginTop: '5px',
              padding: '5px 10px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
            }}
          >
            Estimate API Usage
          </button>
          {estimate && (
            <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#e9ecef', borderRadius: '4px' }}>
              <p style={{ margin: '5px 0', fontSize: '14px' }}>
                Estimated posts/hour: {estimate.estimatedPostsPerHour}
              </p>
              <p style={{ margin: '5px 0', fontSize: '14px' }}>
                Estimated comments/day: {estimate.estimatedCommentsPerDay}
              </p>
              <p style={{ margin: '5px 0', fontSize: '14px' }}>
                Reddit API calls: {estimate.estimatedRedditApiCalls}
              </p>
              <p style={{ margin: '5px 0', fontSize: '14px' }}>
                OpenAI API calls: {estimate.estimatedOpenAiApiCalls}
              </p>
              <p
                style={{
                  margin: '5px 0',
                  fontSize: '14px',
                  color: estimate.withinRateLimits ? '#28a745' : '#dc3545',
                }}
              >
                {estimate.withinRateLimits ? '✓ Within rate limits' : '⚠ Exceeds rate limits'}
              </p>
            </div>
          )}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Minimum Upvotes (optional)
          </label>
          <input
            type="number"
            value={minUpvotes}
            onChange={(e) => setMinUpvotes(e.target.value)}
            min="0"
            style={{ width: '100%', padding: '8px', fontSize: '14px' }}
          />
          <small style={{ color: '#666' }}>Only comment on posts with at least this many upvotes</small>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="checkbox"
              checked={autoApprove}
              onChange={(e) => setAutoApprove(e.target.checked)}
            />
            <span style={{ fontWeight: 'bold' }}>Auto-approve comments (skip manual review)</span>
          </label>
        </div>

        {error && (
          <div style={{ color: 'red', marginBottom: '15px', fontSize: '14px' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '10px 20px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '16px',
            }}
          >
            {loading ? 'Saving...' : isEdit ? 'Update Campaign' : 'Create Campaign'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/campaigns')}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}
